import { format, parseISO, set } from 'date-fns';

/** Datum als TT.MM.JJJJ. Akzeptiert ISO-String oder Date. */
export const formatDatum = (datum: string | Date | null | undefined): string => {
  if (!datum) return '';

  try {
    const d = typeof datum === 'string' ? parseISO(datum) : datum;
    if (Number.isNaN(d.getTime())) return '';
    return format(d, 'dd.MM.yyyy');
  } catch {
    return '';
  }
};

/** Datum im API-Format JJJJ-MM-TT. */
export const alsApiDatum = (datum: Date | null | undefined): string | undefined => {
  if (!datum || Number.isNaN(datum.getTime())) return undefined;
  return format(datum, 'yyyy-MM-dd');
};

/** Uhrzeit als HH:MM. */
export const alsApiZeit = (zeit: Date | null | undefined): string | undefined => {
  if (!zeit || Number.isNaN(zeit.getTime())) return undefined;
  return format(zeit, 'HH:mm');
};

/** "HH:MM" in ein Date des heutigen Tages, für die TimePicker-Komponenten. */
export const zeitAlsDate = (zeit: string | null | undefined): Date | null => {
  if (!zeit) return null;
  const treffer = /^(\d{1,2}):(\d{2})$/.exec(zeit);
  if (!treffer) return null;

  return set(new Date(), {
    hours: Number(treffer[1]),
    minutes: Number(treffer[2]),
    seconds: 0,
    milliseconds: 0
  });
};

/** Stunden mit deutschem Dezimalkomma, z. B. "8,5 h". */
export const formatStunden = (stunden: number | null | undefined): string => {
  if (stunden === null || stunden === undefined || Number.isNaN(stunden)) return '–';
  return `${stunden.toLocaleString('de-DE', { maximumFractionDigits: 2 })} h`;
};

/** Minuten als "45 min" bzw. "–" bei 0. */
export const formatPause = (minuten: number | null | undefined): string => {
  if (!minuten) return '–';
  return `${minuten} min`;
};

/**
 * Restliche Bearbeitungszeit als Text.
 * Admins und Projektleiter sind nicht an die Frist gebunden.
 */
export const formatRestzeit = (
  createdAt: string | null | undefined,
  fristStunden: number,
  unbegrenzt: boolean
): string => {
  if (unbegrenzt) return 'Jederzeit bearbeitbar';
  if (!createdAt) return '–';

  try {
    const erstellt = parseISO(createdAt);
    const verstrichen = (Date.now() - erstellt.getTime()) / (1000 * 60 * 60);
    const restStunden = fristStunden - verstrichen;

    if (restStunden <= 0) return 'Nicht mehr bearbeitbar';

    const tage = Math.floor(restStunden / 24);
    const stunden = Math.floor(restStunden % 24);

    if (tage > 0) {
      return `Noch ${tage} ${tage === 1 ? 'Tag' : 'Tage'}, ${stunden} h`;
    }

    if (stunden > 0) {
      return `Noch ${stunden} h`;
    }

    return `Noch ${Math.floor((restStunden % 1) * 60)} min`;
  } catch {
    return '–';
  }
};
